import React, { useState, useEffect, useRef } from "react";
import { showToast } from "../utils/toast";
import { useNavigate } from "react-router-dom";
import ChatbotComponent from "../components/ChatbotComponent";
import styles from "./ActivityDashboard.module.css";
import { useCallback, memo } from "react";
import { walletService } from "../services/walletService";
import { WalletIcon } from "../assets/icons";
import flagSvg from "../assets/icons/flag.svg";
import coinSvg from "../assets/icons/coin.svg";
import { handleRawFetch401 } from "../utils/apiInterceptor";
import { classifyError, TERMINAL_COPY } from "../utils/errorTaxonomy";
import { resilientFetch, TIMEOUT_NORMAL } from "../utils/resilientFetch";
import DataFallback, { StaleDataBanner } from "../components/feedback/DataFallback";
import clockPng from "../assets/icons/clock.png";
import ActivityHistory from "../components/ActivityHistory";
import BookmarkButton from "../components/BookmarkButton";

/**
 * Every call on this page goes through here so it has a deadline and, for
 * reads, jittered retries. These were bare `fetch`, which is why a deploy
 * surfaced the browser's own "Failed to fetch" in red.
 */
const gsFetch = (url, options = {}, resilience = {}) =>
  resilientFetch(url, options, {
    timeoutMs: TIMEOUT_NORMAL,
    fetchImpl: (u, o) => fetch(u, o),
    ...resilience,
  });

// Helper to check response for 401 and trigger session expired popup
const checkFor401 = (res) => {
  if (res.status === 401) {
    console.log('[ActivityDashboard] 401 detected - triggering popup');
    handleRawFetch401();
    return true;
  }
  return false;
};

const walletSvg = `${process.env.PUBLIC_URL}/images/Wallet icon.svg`;

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Format key-value pairs for display
function formatKeyValuePairs(keyValueData, maxPairs = 5) {
  if (!keyValueData || !Array.isArray(keyValueData)) {
    return [];
  }

  return keyValueData.slice(0, maxPairs).map((pair) => ({
    key: pair.key || "N/A",
    value: pair.value || "N/A",
  }));
}

// Memoized search input component to prevent re-renders of parent on every keystroke
const SearchInput = memo(
  ({ initialValue, onSearch, placeholder, className, loading }) => {
    const [localValue, setLocalValue] = useState(initialValue || "");

    // Sync with parent when initial value changes (e.g. from Clear Filters)
    useEffect(() => {
      setLocalValue(initialValue || "");
    }, [initialValue]);

    // Trigger parent onSearch immediately when value changes;
    // debouncing should be handled by the parent (e.g., via useDebounce).
    useEffect(() => {
      if (localValue === (initialValue || "")) return;
      onSearch(localValue);
    }, [localValue, onSearch, initialValue]);

    return (
      <div className={styles.searchBarWrapper}>
        {!loading && (
          <span className={styles.searchIcon} aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <line
                x1="16.65"
                y1="16.65"
                x2="21"
                y2="21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
        )}
        <input
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={placeholder}
          className={className}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          autoComplete="off"
          spellCheck="false"
          style={{
            outline: "none",
            transition: "all 0.2s ease",
          }}
        />
        {loading && <div className={styles.searchLoader}></div>}
      </div>
    );
  },
);

// Memoized filter modal component to isolate search input from main component re-renders
const FilterModal = memo(
  ({
    isOpen,
    onClose,
    filterCategory,
    filterCreditsRange,
    creditsBounds,
    filterDurationRange,
    durationBounds,
    onApplyFilters,
    categories,
  }) => {
    const [localCategory, setLocalCategory] = useState(filterCategory);
    const [localCreditsRange, setLocalCreditsRange] =
      useState(filterCreditsRange);
    const [localDurationRange, setLocalDurationRange] =
      useState(filterDurationRange);

    useEffect(() => {
      if (!isOpen) return;
      setLocalCategory(filterCategory);
      setLocalCreditsRange(filterCreditsRange);
      setLocalDurationRange(filterDurationRange);
    }, [isOpen, filterCategory, filterCreditsRange, filterDurationRange]);

    const [creditsMin, creditsMax] = localCreditsRange;
    const [durationMin, durationMax] = localDurationRange;

    const formatDays = (value) => `${value} ${value === 1 ? "Day" : "Days"}`;

    if (!isOpen) return null;

    return (
      <div className={styles.filterOverlay}>
        <div className={styles.filterModal}>
          {/* Header with close button */}
          <div className={styles.filterHeader}>
            <h2 className={styles.filterTitle}>Filter Activities</h2>
            <button onClick={onClose} className={styles.filterClose}>
              ×
            </button>
          </div>

          {/* Filter Content */}
          <div className={styles.filterContent}>
            <div className={styles.filterForm}>
              {/* Activity Category Filter */}
              <div className={styles.filterSection}>
                <label className={styles.filterLabel}>Activity Category</label>
                <select
                  value={localCategory}
                  onChange={(e) => {
                    setLocalCategory(e.target.value);
                  }}
                  className={styles.selectInput}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterSection}>
                <div className={styles.rangeHeader}>
                  <label className={styles.filterLabel}>Credits</label>
                  <span className={styles.rangeValue}>
                    {creditsMin} - {creditsMax}
                  </span>
                </div>
                <div className={styles.dualRangeWrap}>
                  <input
                    className={styles.dualRange}
                    type="range"
                    min={creditsBounds.min}
                    max={creditsBounds.max}
                    value={creditsMin}
                    onChange={(e) =>
                      setLocalCreditsRange([
                        Math.min(Number(e.target.value), creditsMax),
                        creditsMax,
                      ])
                    }
                  />
                  <input
                    className={styles.dualRange}
                    type="range"
                    min={creditsBounds.min}
                    max={creditsBounds.max}
                    value={creditsMax}
                    onChange={(e) =>
                      setLocalCreditsRange([
                        creditsMin,
                        Math.max(Number(e.target.value), creditsMin),
                      ])
                    }
                  />
                </div>
              </div>

              <div className={styles.filterSection}>
                <div className={styles.rangeHeader}>
                  <label className={styles.filterLabel}>Duration</label>
                  <span className={styles.rangeValue}>
                    {formatDays(durationMin)} - {formatDays(durationMax)}
                  </span>
                </div>
                <div className={styles.dualRangeWrap}>
                  <input
                    className={styles.dualRange}
                    type="range"
                    min={durationBounds.min}
                    max={durationBounds.max}
                    step={1}
                    value={durationMin}
                    onChange={(e) =>
                      setLocalDurationRange([
                        Math.min(Number(e.target.value), durationMax),
                        durationMax,
                      ])
                    }
                  />
                  <input
                    className={styles.dualRange}
                    type="range"
                    min={durationBounds.min}
                    max={durationBounds.max}
                    step={1}
                    value={durationMax}
                    onChange={(e) =>
                      setLocalDurationRange([
                        durationMin,
                        Math.max(Number(e.target.value), durationMin),
                      ])
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.filterActions}>
            <button
              onClick={() => {
                setLocalCategory("");
                setLocalCreditsRange([creditsBounds.min, creditsBounds.max]);
                setLocalDurationRange([durationBounds.min, durationBounds.max]);
              }}
              className={styles.clearButton}
            >
              Clear Filters
            </button>
            <button
              onClick={() =>
                onApplyFilters({
                  category: localCategory,
                  creditsRange: localCreditsRange,
                  durationRange: localDurationRange,
                })
              }
              className={styles.applyButton}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    );
  },
);

// Placeholder: import or define your payment gateway integration function
// import { initiatePayment } from '../utils/payment';
// function initiatePayment(payment) {
//   // payment: { payment_url, payment_data }
//   console.log('[PayU] initiatePayment called with:', payment);
//   if (!payment || !payment.payment_url || !payment.payment_data) {
//     showToast({ type: 'error', message: 'Payment gateway data missing.' });
//     return;
//   }
//   // Create and auto-submit a form to PayU
//   const form = document.createElement('form');
//   form.method = 'POST';
//   form.action = payment.payment_url;
//   form.target = '_self'; // Ensure navigation in same tab
//   Object.entries(payment.payment_data).forEach(([key, value]) => {
//     const input = document.createElement('input');
//     input.type = 'hidden';
//     input.name = key;
//     input.value = value;
//     form.appendChild(input);
//   });
//   document.body.appendChild(form);
//   console.log('[PayU] Form created and appended:', form, form.action, form.method, form.target);
//   // Use setTimeout to avoid React event interference
//   setTimeout(() => {
//     console.log('[PayU] Submitting form now...');
//     try {
//       form.submit();
//       console.log('[PayU] Form submitted.');
//     } catch (err) {
//       console.error('[PayU] Form submit error:', err);
//     }
//     // Do not remove the form after submit, let browser handle navigation
//   }, 0);
// }

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ActivityDashboard() {
  const navigate = useNavigate();

  // API state
  const [activities, setActivities] = useState({
    available: [],
    claimed: [],
    pending: [],
    cancelled: [],
  });
  const [calendarActivities, setCalendarActivities] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When the activities on screen were last confirmed current, for the notice
  // shown while a refresh cannot get through.
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showActivityDetailsModal, setShowActivityDetailsModal] =
    useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Build KPIs with executions for a given activity from claimedActivities
  const buildKpisWithExecutionsForActivity = useCallback(
    (activityId) => {
      try {
        const claimsForActivity = (activities.claimed || []).filter(
          (c) => (c.activity_id || c.activity?.id) === activityId,
        );
        const kpiIdToKpi = new Map();

        claimsForActivity.forEach((claim) => {
          const claimId = claim.claim_id || claim.id;
          const executionDate = claim.execution_date;
          const status = claim.status;

          (claim.kpis || []).forEach((k) => {
            const kpiId = k.id || k.kpi_id;
            if (!kpiId) return;

            if (!kpiIdToKpi.has(kpiId)) {
              kpiIdToKpi.set(kpiId, {
                id: kpiId,
                name: k.name || k.kpi,
                description: k.description || "",
                measurement_unit: k.measurement_unit,
                input_type: k.input_type,
                options: k.options,
                is_required: k.is_required,
                executions: [],
              });
            }

            const kpiEntry = kpiIdToKpi.get(kpiId);
            kpiEntry.executions.push({
              claim_id: claimId,
              execution_date: executionDate,
              status: status,
              value: k.value,
            });
          });
        });

        // Sort executions (most recent first)
        kpiIdToKpi.forEach((kpi) => {
          kpi.executions.sort(
            (a, b) => new Date(b.execution_date) - new Date(a.execution_date),
          );
        });

        return Array.from(kpiIdToKpi.values());
      } catch (err) {
        return [];
      }
    },
    [activities.claimed],
  );

  // Filter state
  const [filterCategory, setFilterCategory] = useState("");
  // const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCreditsRange, setFilterCreditsRange] = useState([
    0,
    Number.MAX_SAFE_INTEGER,
  ]);
  const [filterDurationRange, setFilterDurationRange] = useState([
    0,
    Number.MAX_SAFE_INTEGER,
  ]);
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    favourites: 0,
    claimed: 0,
    pending: 0,
    cancelled: 0,
  });
  const [tabItems, setTabItems] = useState([]);
  const [pageSize] = useState(12);
  // Tabs state
  const [tab, setTab] = useState("all");

  // Brand and package state
  const [brand, setBrand] = useState(null);

  // Categories state
  const [categories, setCategories] = useState([]);
  // Claim activity handler
  const [claiming, setClaiming] = useState(false);
  const [claimDate, setClaimDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  const [walletInfo, setWalletInfo] = useState(null);

  // Banner state
  const [bannerData, setBannerData] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(false);

  // Responsive state
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Activities stay on screen for as long as the outage lasts, with the banner
  // stating exactly when they were true — this is a browsing surface, and an
  // error screen in place of a catalogue someone was reading helps nobody.
  const isStale = Boolean(error) && (tabItems || []).length > 0;

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      let url = `${API_BASE_URL}/api/activities/fetch/`;
      const params = [];
      const apiTab = tab === "flagged" ? "favourites" : tab;

      if (filterCategory && filterCategory !== "") {
        // Backend expects category_name (filters on category__name)
        params.push(`category_name=${encodeURIComponent(filterCategory)}`);
      }
      // if (filterStatus !== '') params.push(`is_active=${filterStatus}`);
      if (debouncedSearchQuery.trim())
        params.push(
          `search=${encodeURIComponent(debouncedSearchQuery.trim())}`,
        );
      const isCreditsFilterActive =
        clampedCreditsRange[0] > 0 ||
        clampedCreditsRange[1] < creditsBounds.max;
      const isDurationFilterActive =
        clampedDurationRange[0] > 0 ||
        clampedDurationRange[1] < durationBounds.max;
      const exactDurationValue =
        clampedDurationRange[0] === clampedDurationRange[1]
          ? clampedDurationRange[0]
          : null;

      if (isCreditsFilterActive) {
        params.push(
          `min_credits=${encodeURIComponent(clampedCreditsRange[0])}`,
        );
        params.push(
          `max_credits=${encodeURIComponent(clampedCreditsRange[1])}`,
        );
      }

      if (isDurationFilterActive) {
        if (exactDurationValue !== null) {
          params.push(
            `duration_days=${encodeURIComponent(exactDurationValue)}`,
          );
        } else {
          params.push(
            `duration_min=${encodeURIComponent(clampedDurationRange[0])}`,
          );
          params.push(
            `duration_max=${encodeURIComponent(clampedDurationRange[1])}`,
          );
        }
      }
      params.push(`tab=${encodeURIComponent(apiTab)}`);
      params.push(`limit=${pageSize}`);
      params.push(`offset=${currentPage * pageSize}`);

      if (params.length) url += "?" + params.join("&");

      console.log("Fetching activities with URL:", url);

      const res = await gsFetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        // Handle 401 errors globally
        if (res.status === 401) {
          console.log('[ActivityDashboard] 401 detected in fetchActivities');
          handleRawFetch401();
          throw new Error('Session expired');
        }
        
        const errorText = await res.text();
        console.error("API Error:", res.status, errorText);
        // Carry the status rather than baking it into the message: the status is
        // for us, and the copy the user sees comes from classifyError.
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }

      const data = await res.json();
      console.log("API response JSON:", data);

      if (data.message) {
        console.log("API Response Message:", data.message);
      }

      if (data.status === "success") {
        setTotalCount(data.total_count || 0);
        setTabCounts({
          all: data.all_count || 0,
          favourites: data.favourites_count || 0,
          claimed: data.claimed_count || 0,
          pending: data.pending_count || 0,
          cancelled: data.cancelled_count || 0,
        });
        setTabItems(Array.isArray(data.tab_items) ? data.tab_items : []);

        const available = data.activities || [];
        const claimed = data.claimed_activities || [];
        const pending = data.pending_claims || [];
        const cancelled = data.cancelled_claims || [];

        setActivities({
          available,
          claimed,
          pending,
          cancelled,
        });
        setError(null);
        setLastLoadedAt(Date.now());
      } else {
        // A 200 whose body reports a problem. Prefer the backend's own sentence,
        // which is written for a person, and fall back to the shared copy rather
        // than inventing a new phrasing here.
        throw new Error(data.message || TERMINAL_COPY.unknown.message);
      }
    } catch (e) {
      // Keep the classified description, not the raw message: during a deploy
      // that message is the browser's own "Failed to fetch", which told the user
      // nothing they could act on.
      setError(e && e.classified ? e.classified : classifyError(e));
      // Deliberately leaves the counts and lists as they are. Zeroing them
      // stated as fact something we had just failed to find out — the tabs would
      // read "Claimed 0" when the truth was that we could not ask — and it threw
      // away rows the user was already reading. Whatever was last known stays
      // until a request succeeds and replaces it.
    } finally {
      setLoading(false);
    }
  };

  // Fetch brand and package details
  const fetchBrandAndPackage = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await gsFetch(`${API_BASE_URL}/api/auth/users/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        // Handle 401 errors globally
        if (res.status === 401) {
          console.log('[ActivityDashboard] 401 detected in fetchBrandAndPackage');
          handleRawFetch401();
          throw new Error('Session expired');
        }
        throw new Error("Failed to fetch user details");
      }

      const data = await res.json();

      setBrand(data.user?.brand || null);
    } catch (e) {
      setBrand(null);
    }
  };

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const res = await gsFetch(`${API_BASE_URL}/api/activities/categories/`);
      if (!res.ok) throw new Error("Failed to fetch categories");
      const data = await res.json();
      if (data.status === "success") {
        const normalizedCategories = (data.categories || []).map((category) => {
          let normalizedLogo = category.logo;

          if (normalizedLogo && normalizedLogo.startsWith("/")) {
            normalizedLogo = `${API_BASE_URL}${normalizedLogo}`;
          }

          return {
            ...category,
            logo: normalizedLogo,
          };
        });

        setCategories(normalizedCategories);
      }
    } catch (e) {
      console.error("Error fetching categories:", e);
      setCategories([]);
    }
  };

  // Fetch calendar activities from backend
  const fetchCalendarActivities = async (params = {}) => {
    try {
      setCalendarLoading(true);
      setCalendarError(null);
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.date) queryParams.append("date", params.date);
      if (params.start_date)
        queryParams.append("start_date", params.start_date);
      if (params.end_date) queryParams.append("end_date", params.end_date);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/activities/calendar/list/${queryString ? "?" + queryString : ""}`;

      const res = await gsFetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (checkFor401(res)) return;
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch calendar activities: ${res.status}`,
        );
      }

      const data = await res.json();
      // Handle various possible response structures from the API
      let activities = [];

      // Try different possible structures based on your API response
      if (
        data.status === "success" &&
        data.data &&
        Array.isArray(data.data.data)
      ) {
        // Structure: { status: "success", data: { data: [...] } }
        activities = data.data.data;
      } else if (data.status === "success" && Array.isArray(data.data)) {
        // Structure: { status: "success", data: [...] }
        activities = data.data;
      } else if (Array.isArray(data.data?.data)) {
        // Alternative structure: { data: { data: [...] } }
        activities = data.data.data;
      } else if (Array.isArray(data.data)) {
        // Alternative structure: { data: [...] }
        activities = data.data;
      } else if (Array.isArray(data.activities)) {
        // Fallback: { activities: [...] }
        activities = data.activities;
      } else {
        // If none of the structures match, log the response for debugging
        console.warn("Unexpected API response structure:", data);
        activities = [];
      }

      setCalendarActivities(activities);
    } catch (e) {
      console.error("Error fetching calendar activities:", e);
      setCalendarError(e.message || "Failed to load activities");
      setCalendarActivities([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Create calendar activity
  const createCalendarActivity = async (formData) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const res = await gsFetch(`${API_BASE_URL}/api/activities/calendar/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to create activity: ${res.status}`,
        );
      }

      const data = await res.json();
      let activity = null;

      // Handle various possible response structures
      if (data.status === "success" && data.data) {
        // Structure: { status: "success", data: {...} }
        activity = data.data;
      } else if (data.status === "success" && data.activity) {
        // Fallback: { status: "success", activity: {...} }
        activity = data.activity;
      } else if (data.data) {
        // Structure: { data: {...} }
        activity = data.data;
      } else {
        // Fallback: direct activity object
        activity = data;
      }

      if (data.status === "success" || activity) {
        // Refresh calendar activities after creation
        await fetchCalendarActivities();
        return activity;
      } else {
        throw new Error(data.message || "Failed to create activity");
      }
    } catch (e) {
      console.error("Error creating calendar activity:", e);
      throw e;
    }
  };

  // Update calendar activity
  const updateCalendarActivity = async (id, formData) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const res = await gsFetch(
        `${API_BASE_URL}/api/activities/calendar/${id}/update/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to update activity: ${res.status}`,
        );
      }

      const data = await res.json();
      let activity = null;

      // Handle various possible response structures
      if (data.status === "success" && data.data) {
        // Structure: { status: "success", data: {...} }
        activity = data.data;
      } else if (data.status === "success" && data.activity) {
        // Fallback: { status: "success", activity: {...} }
        activity = data.activity;
      } else if (data.data) {
        // Structure: { data: {...} }
        activity = data.data;
      } else {
        // Fallback: direct activity object
        activity = data;
      }

      if (data.status === "success" || activity) {
        // Refresh calendar activities after update
        await fetchCalendarActivities();
        return activity;
      } else {
        throw new Error(data.message || "Failed to update activity");
      }
    } catch (e) {
      console.error("Error updating calendar activity:", e);
      throw e;
    }
  };

  // Delete calendar activity
  const deleteCalendarActivity = async (id) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const res = await gsFetch(
        `${API_BASE_URL}/api/activities/calendar/${id}/delete/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to delete activity: ${res.status}`,
        );
      }

      const data = await res.json();
      if (data.status === "success" || data.message) {
        // Refresh calendar activities after deletion
        await fetchCalendarActivities();
        return data;
      } else {
        throw new Error(data.message || "Failed to delete activity");
      }
    } catch (e) {
      console.error("Error deleting calendar activity:", e);
      throw e;
    }
  };

  // Fetch plan banner from backend
  const fetchPlanBanner = async () => {
    try {
      setBannerLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        console.warn("No token available for banner fetch");
        setBannerData(null);
        return;
      }

      const res = await gsFetch(`${API_BASE_URL}/api/activities/banner/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Handle 404 as expected (no banner configured for this plan)
      if (res.status === 404) {
        console.log("No banner configured for this plan, using default");
        setBannerData(null);
        return;
      }

      if (!res.ok) {
        throw new Error(`Failed to fetch banner: ${res.status}`);
      }

      const data = await res.json();

      if (data.status === "success" && data.banner) {
        // Handle both absolute and relative URLs
        let bannerUrl = data.banner.banner_url;
        if (
          bannerUrl &&
          !bannerUrl.startsWith("http://") &&
          !bannerUrl.startsWith("https://")
        ) {
          // If relative URL, prepend API base URL
          bannerUrl = bannerUrl.startsWith("/")
            ? `${API_BASE_URL}${bannerUrl}`
            : `${API_BASE_URL}/${bannerUrl}`;
        }

        setBannerData({
          banner_url: bannerUrl,
          title: data.banner.title,
          description: data.banner.description,
        });
      } else {
        // API returned success but no banner data
        setBannerData(null);
      }
    } catch (e) {
      console.error("Error fetching plan banner:", e);
      // On error, fallback to default banner
      setBannerData(null);
    } finally {
      setBannerLoading(false);
    }
  };

  const claimActivity = async (activity, executionDate) => {
    try {
      setClaiming(true);

      if (!activity.claimable) {
        showToast({ type: "error", message: "Activity is not claimable." });
        return;
      }

      if (!executionDate) {
        showToast({
          type: "error",
          message: "Please select an execution date.",
        });
        return;
      }

      const token = localStorage.getItem("token");
      const res = await gsFetch(
        `${API_BASE_URL}/api/activities/claim/${activity.id}/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            activity_id: activity.id,
            execution_date: executionDate,
          }),
        },
      );

      const data = await res.json();

      if (res.ok && data.status === "success") {
        showToast({
          type: "success",
          message: data.message || "Growth Suite activity purchased!",
        });
        setShowClaimModal(false);
        await fetchActivities();

        // // Refetch user info for updated credits
        // const userRes = await gsFetch(`${API_BASE_URL}/auth/users/`, { headers: { 'Authorization': `Bearer ${token}` } });

        // if (userRes.ok) {
        //     const userData = await userRes.json();
        // }
      } else if (data.status === "payment_required") {
        showToast({
          type: "info",
          message: "Insufficient credits. Please buy more credits.",
        });
        navigate(`/wallet?brand_id=${brand.id}`);
      } else {
        showToast({
          type: "error",
          message:
            data.message || data.reason || "Failed to purchase activity.",
        });
      }
    } catch (e) {
      showToast({ type: "error", message: "Failed to purchase activity." });
    } finally {
      setClaiming(false);
    }
  };

  const fetchWalletData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || !brand || !brand.id) {
        return; // Brand not loaded yet or no token
      }
      const balance = await walletService.checkWalletBalance(token, brand.id);
      console.log("Wallet balance:", balance);
      setWalletInfo(balance);
    } catch (error) {
      console.error("Error fetching wallet data:", error);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    fetchBrandAndPackage();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterCategory,
    debouncedSearchQuery,
    filterCreditsRange,
    filterDurationRange,
  ]);

  // Fetch activities when parameters change
  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    filterCategory,
    debouncedSearchQuery,
    filterCreditsRange,
    filterDurationRange,
    pageSize,
    currentPage,
  ]);

  // Fetch wallet data when brand is available/changes
  useEffect(() => {
    fetchWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id]);

  // Fetch plan banner when brand/subscription plan is available
  useEffect(() => {
    if (brand?.subscription_plan) {
      fetchPlanBanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.subscription_plan?.id]);

  // Ensure currentPage is within valid bounds when totalCount changes
  useEffect(() => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages > 0 && currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalCount, pageSize, currentPage]);

  // Pending claim actions state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [claimToCancel, setClaimToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const cancelReasonRef = useRef("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [claimToEdit, setClaimToEdit] = useState(null);
  const [newExecutionDate, setNewExecutionDate] = useState("");
  const [editingDate, setEditingDate] = useState(false);
  const [editError, setEditError] = useState(null);

  // Filtered lists
  const allActivitiesForBounds = [
    ...(activities.available || []),
    ...(activities.claimed || []),
    ...(activities.pending || []),
    ...(activities.cancelled || []),
  ];

  const creditValues = allActivitiesForBounds
    .map((activity) =>
      Number(activity?.credit_cost ?? activity?.credits_used ?? 0),
    )
    .filter((value) => Number.isFinite(value));

  const durationValues = allActivitiesForBounds
    .map((activity) => Number(activity?.activity_duration ?? 0))
    .filter((value) => Number.isFinite(value));

  const creditsBounds = {
    min: 0,
    max: Math.max(
      500,
      ...(creditValues.length ? [Math.ceil(Math.max(...creditValues))] : [0]),
    ),
  };

  const durationBounds = {
    min: 0,
    max: Math.max(
      500,
      ...(durationValues.length
        ? [Math.ceil(Math.max(...durationValues))]
        : [0]),
    ),
  };

  const clampedCreditsRange = [
    Math.max(
      creditsBounds.min,
      Math.min(filterCreditsRange[0], creditsBounds.max),
    ),
    Math.max(
      creditsBounds.min,
      Math.min(filterCreditsRange[1], creditsBounds.max),
    ),
  ];

  const clampedDurationRange = [
    Math.max(
      durationBounds.min,
      Math.min(filterDurationRange[0], durationBounds.max),
    ),
    Math.max(
      durationBounds.min,
      Math.min(filterDurationRange[1], durationBounds.max),
    ),
  ];
  //   const lockedActivities = activitiesOld.locked.map(a => ({ ...a, locked: true }));

  // Load calendar activities when component mounts and when calendar tab is selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchCalendarActivities();
  }, []); // Load on component mount

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "calendar") {
      fetchCalendarActivities();
    }
  }, [tab]);

  // Handlers
  const handleClaim = (activity) => {
    setSelectedActivity(activity);
    // Reset claim date to today's date each time the modal is opened
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setClaimDate(`${yyyy}-${mm}-${dd}`);
    setShowClaimModal(true);
  };
  const handleFilter = () => setShowFilterModal(true);
  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    setCurrentPage(0);
  };
  const handleChatMessage = (message) => {
    // TODO: Implement chatbot functionality
    console.log("Chat message:", message);
  };

  const handleFlagToggle = async (activity, flag) => {
    const token = localStorage.getItem("token");
    const url = flag
      ? `${API_BASE_URL}/api/activities/unflag/`
      : `${API_BASE_URL}/api/activities/flag/`;
    try {
      const response = await gsFetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ activity_id: activity.id }),
      });
      if (!response.ok) {
        throw new Error(`Failed to ${flag ? "unflag" : "flag"} activity`);
      }
      await fetchActivities();
    } catch (e) {
      alert("Failed to update flag status");
    }
  };

  const openCancelModal = (claim) => {
    setClaimToCancel(claim);
    cancelReasonRef.current = "";
    setCancelModalOpen(true);
  };

  const handleCancelClaim = async () => {
    if (!claimToCancel || cancelling) return;
    try {
      setCancelling(true);
      const token = localStorage.getItem("token");
      const res = await gsFetch(
        `${API_BASE_URL}/api/activities/claims/${claimToCancel.claim_id}/cancel/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: cancelReasonRef.current || "" }),
        },
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.status !== "success") {
        showToast({
          type: "error",
          message:
            data.message || data.error || "Failed to cancel activity claim.",
        });
        return;
      }

      showToast({
        type: "success",
        message: data.message || "Activity claim cancelled successfully.",
      });

      setActivities((prev) => ({
        ...prev,
        pending: (prev.pending || []).filter(
          (c) => c.claim_id !== claimToCancel.claim_id,
        ),
      }));
      setCancelModalOpen(false);
      setClaimToCancel(null);
      cancelReasonRef.current = "";
      await fetchActivities();
    } catch (e) {
      showToast({
        type: "error",
        message: "Failed to cancel activity claim.",
      });
    } finally {
      setCancelling(false);
    }
  };

  const openEditModal = (claim) => {
    setClaimToEdit(claim);
    setNewExecutionDate(claim.execution_date || "");
    setEditError(null);
    setEditModalOpen(true);
  };

  const handleUpdateExecutionDate = async () => {
    if (!claimToEdit || editingDate) return;

    if (!newExecutionDate) {
      setEditError("Please select a new execution date.");
      return;
    }

    try {
      setEditingDate(true);
      setEditError(null);
      const token = localStorage.getItem("token");
      const res = await gsFetch(
        `${API_BASE_URL}/api/activities/claims/${claimToEdit.claim_id}/update-date/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ execution_date: newExecutionDate }),
        },
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.status !== "success") {
        const message =
          data.message ||
          data.error ||
          "Failed to update execution date. Please try a different date.";
        setEditError(message);
        showToast({ type: "error", message });
        return;
      }

      showToast({
        type: "success",
        message: data.message || "Execution date updated successfully.",
      });

      setActivities((prev) => ({
        ...prev,
        pending: (prev.pending || []).map((c) =>
          c.claim_id === claimToEdit.claim_id
            ? { ...c, execution_date: newExecutionDate }
            : c,
        ),
      }));

      setEditModalOpen(false);
      setClaimToEdit(null);
      setNewExecutionDate("");
    } catch (e) {
      const message = "Failed to update execution date.";
      setEditError(message);
      showToast({ type: "error", message });
    } finally {
      setEditingDate(false);
    }
  };

  // (duplicate pending-claim handlers were removed)

  // Simple modal component
  const Modal = ({ open, onClose, title, children, width, height }) => {
    if (!open) return null;
    const isLargeModal = width === "85%";
    return (
      <div className={styles.modalOverlay}>
        <div
          className={`${styles.modalContent} ${isLargeModal ? styles.modalContentWide : ""}`}
          style={{ width: width || "45%" }}
        >
          {title && (
            <>
              <button onClick={onClose} className={styles.modalClose}>
                ×
              </button>
              <div className={styles.modalTitle}>{title}</div>
            </>
          )}
          {children}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Marketing Suite Dashboard */}
      <div className={styles.dashboardContainer}>
        {/* Dashboard Body */}
        <div className={styles.dashboardBody}>
          {/* Main Content */}
          <main className={styles.mainContent}>
            {/* Main Header */}
            <header
              className={styles.mainHeader}
              style={{
                alignItems: windowWidth > 768 ? "center" : "flex-start",
                flexDirection: windowWidth > 768 ? "row" : "column",
                gap: windowWidth > 768 ? "0" : "20px",
              }}
            >
              <div
                className="welcome-text"
                style={{ marginLeft: windowWidth > 768 ? "14px" : "0" }}
              >
                <h1
                  style={{
                    fontSize: windowWidth > 768 ? "36px" : "28px",
                    fontWeight: 700,
                    color: "#000000",
                    display: "inline-block",
                  }}
                >
                  {/* Show 'Welcome' immediately; append comma + brand name only when available to avoid flicker */}
                  Welcome
                  {brand?.name ? (
                    <>
                      <span>, </span>
                      <span>{brand.name}!</span>
                    </>
                  ) : null}
                </h1>
                <p
                  style={{
                    fontSize: windowWidth > 768 ? "26px" : "18px",
                    color: "#1b1d1e",
                    marginTop: "8px",
                  }}
                >
                  Your Brand's Marketing Journey - All in One Place
                </p>
              </div>
            </header>

            {/* Content Layout */}
            <div className={styles.contentLayout}>
              {/* Primary Column */}
              <div className={styles.primaryColumn}>
                {/* Growth Banner */}
                <section
                  className={styles.growthBanner}
                  style={{
                    backgroundImage: bannerData?.banner_url
                      ? `url("${bannerData.banner_url}")`
                      : 'url("/images/growth-suite-bg.png")',
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: "48px",
                    padding: "40px",
                    color: "#ffffff",
                  }}
                >
                  <div
                    className=""
                    style={{
                      position: "absolute",
                      top: "25%",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: "#ffffff",
                        display: "block",
                        marginTop: "1rem",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        caretColor: "transparent",
                        cursor: "default",
                      }}
                    >
                      {bannerData?.title ||
                        "How Growth Suite Works (3 Easy Steps)"}
                    </span>
                    <p
                      style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        opacity: 0.9,
                        marginBottom: "24px",
                        color: "#ffffff",
                        margin: "8px 0 24px 0",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        caretColor: "transparent",
                        cursor: "default",
                      }}
                    >
                      {bannerData?.description ||
                        "Choose Your Objective → Claim an Activity → Receive Accelerated Growth"}
                    </p>
                  </div>
                </section>

                {/* add  */}

                {/* Marketing Suite */}
                <section className={styles.marketingSuite}>
                  {/* Suite Header */}
                  <header className={styles.suiteHeaderCol}>
                    <div className={styles.suiteTitleWrapper}>
                      <h2 className={styles.suiteTitle}>
                        Suspire Growth Suite
                      </h2>
                      <p className={styles.suiteSubtitle}>
                        A curated catalog of promotional activities you can
                        claim on demand
                      </p>
                    </div>

                    {/* Search and Filters Row */}
                    <div className={styles.searchFilterContainer}>
                      <SearchInput
                        initialValue={searchQuery}
                        onSearch={setSearchQuery}
                        placeholder="Search activities..."
                        className={styles.searchInputCustom}
                        loading={loading}
                      />
                      <button
                        className={styles.filterButton}
                        onClick={handleFilter}
                        title="Filter activities"
                      >
                        <img
                          src="/images/filter-sliders.svg"
                          alt="filter icon"
                        />
                      </button>
                    </div>
                  </header>

                  {/* Suite Tabs */}
                  <nav className="suite-tabs">
                    <ul className={styles.suiteTabs}>
                      <li>
                        <button
                          className={`${styles.tabItem} ${tab === "all" ? styles.tabItemActive : ""}`}
                          onClick={() => handleTabChange("all")}
                        >
                          All{" "}
                          <span className={styles.tabBadgeGreen}>
                            {tabCounts.all}
                          </span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${styles.tabItem} ${tab === "flagged" ? styles.tabItemActive : ""}`}
                          onClick={() => handleTabChange("flagged")}
                        >
                          Favourites{" "}
                          <span className={styles.tabBadgeRed}>
                            {tabCounts.favourites}
                          </span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${styles.tabItem} ${tab === "claimed" ? styles.tabItemActive : ""}`}
                          onClick={() => handleTabChange("claimed")}
                        >
                          Claimed{" "}
                          <span className={styles.tabBadgeGreen}>
                            {tabCounts.claimed}
                          </span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${styles.tabItem} ${tab === "pending" ? styles.tabItemActive : ""}`}
                          onClick={() => handleTabChange("pending")}
                        >
                          Pending{" "}
                          <span className={styles.tabBadgeGreen}>
                            {tabCounts.pending}
                          </span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${styles.tabItem} ${tab === "cancelled" ? styles.tabItemActive : ""}`}
                          onClick={() => handleTabChange("cancelled")}
                        >
                          Cancelled{" "}
                          <span className={styles.tabBadgeRed}>
                            {tabCounts.cancelled}
                          </span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`${styles.tabItem} ${tab === "calendar" ? styles.tabItemActive : ""}`}
                          onClick={() => setTab("calendar")}
                        >
                          Activity Calendar{" "}
                          <span className={styles.tabBadgeGreen}>
                            {calendarActivities?.length || 0}
                          </span>
                        </button>
                      </li>
                    </ul>
                  </nav>

                  {/* Suite Cards Section */}
                  {tab === "calendar" ? (
                    <ActivityHistory
                      activities={calendarActivities}
                      loading={calendarLoading}
                      error={calendarError}
                    />
                  ) : (
                    <div
                      className={styles.suiteCards}
                      style={{
                        opacity: loading ? 0.6 : 1,
                        pointerEvents: loading ? "none" : "auto",
                        transition: "opacity 0.2s ease",
                        gridTemplateColumns:
                          windowWidth > 1440 ? "repeat(2, 1fr)" : "1fr",
                        minHeight: "400px",
                      }}
                    >
                      {/*
                        One outcome per render. An empty list means something
                        different depending on why it is empty: "nothing matched"
                        is only true once a request has actually come back, and
                        saying it while a request is failing tells the user the
                        catalogue is empty when we simply could not read it.
                      */}
                      {isStale && (
                        <StaleDataBanner
                          since={lastLoadedAt}
                          onRetry={fetchActivities}
                        />
                      )}
                      {!loading && error && !tabItems.length ? (
                        <DataFallback
                          variant="table"
                          error={error}
                          onRetry={fetchActivities}
                        />
                      ) : loading && !tabItems.length ? (
                        <div
                          style={{
                            padding: "60px",
                            textAlign: "center",
                            color: "#fff",
                            gridColumn: "1 / -1",
                          }}
                        >
                          <div
                            className={styles.searchLoader}
                            style={{
                              margin: "0 auto 16px",
                              position: "static",
                            }}
                          ></div>
                          Loading activities...
                        </div>
                      ) : (
                        (() => {
                          const filteredActivities = tabItems || [];

                          const normalizedQuery = (
                            debouncedSearchQuery || searchQuery || ""
                          )
                            .trim()
                            .toLowerCase();

                        if (filteredActivities.length === 0) {
                          return (
                            <div className={styles.noResults}>
                              <span className={styles.noResultsIcon}>🔍</span>
                              <h3 className={styles.noResultsTitle}>
                                No activities found
                              </h3>
                              <p className={styles.noResultsText}>
                                {normalizedQuery
                                  ? `We couldn't find any activities matching "${searchQuery}"`
                                  : "There are no activities available in this category at the moment."}
                              </p>
                            </div>
                          );
                        }

                        return filteredActivities.map((activity, index) => {
                          const nameSafe =
                            activity?.name || activity?.activity_name || "";
                          const maxNameLen = 24;
                          const displayName =
                            nameSafe.length > maxNameLen
                              ? `${nameSafe.slice(0, maxNameLen).trim()}...`
                              : nameSafe;
                          const hasFeatureTag = Boolean(
                            activity?.feature_tag?.tag_name,
                          );

                          const getCategoryTag = () => {
                            // Keep calendar mapping compatibility while preserving claimed/pending mapping.
                            if (tab === "calendar") {
                              const category =
                                activity.category ||
                                activity.activity_category ||
                                "General";
                              const categoryData = categories.find(
                                (c) => c.name === category,
                              ) || {
                                name: category,
                                background_color: "#e0e0e0",
                                text_color: "#333",
                                logo: "/images/activity-icons/default.png",
                              };
                              return {
                                text: categoryData.name,
                                bg: categoryData.background_color,
                                color: categoryData.text_color,
                                border: categoryData.text_color,
                                icon: categoryData.logo,
                              };
                            }

                            const category =
                              tab === "claimed" ||
                              tab === "pending" ||
                              tab === "cancelled"
                                ? activity.activity_category
                                : activity.category;
                            const categoryData = categories.find(
                              (c) => c.name === category,
                            ) || {
                              name: category || "General",
                              background_color: "#e0e0e0",
                              text_color: "#333",
                              logo: "/images/activity-icons/default.png",
                            };
                            return {
                              text: categoryData.name,
                              bg: categoryData.background_color,
                              color: categoryData.text_color,
                              border: categoryData.text_color,
                              icon: categoryData.logo,
                            };
                          };

                          const categoryTag = getCategoryTag();
                          const activityId =
                            activity.id ||
                            activity.activity_id ||
                            activity.claim_id;

                          return (
                            <div
                              key={activityId}
                              className={styles.activityCard}
                              style={{ opacity: activity.locked ? 0.7 : 1, position: 'relative' }}
                              onClick={() => {
                                const mappedActivity = {
                                  ...activity,
                                  id: activityId,
                                  activity_id:
                                    activity.activity_id || activityId,
                                  activity_name:
                                    activity.activity_name || activity.name,
                                  activity_description:
                                    activity.activity_description ||
                                    activity.description,
                                  activity_category:
                                    activity.activity_category ||
                                    activity.category,
                                  activity_duration:
                                    activity.activity_duration ||
                                    activity.claimed_activity_duration,
                                  execution_date:
                                    activity.execution_date ||
                                    activity.claimed_execution_date,
                                  execution_start_date:
                                    activity.execution_start_date,
                                  created_at:
                                    activity.created_at ||
                                    activity.claimed_created_at,
                                  kpis: buildKpisWithExecutionsForActivity(
                                    activityId,
                                  ),
                                };
                                setSelectedActivity(mappedActivity);
                                setShowClaimModal(false);
                                setShowFilterModal(false);
                                setShowActivityDetailsModal(false);

                                setTimeout(
                                  () => setShowActivityDetailsModal(true),
                                  0,
                                );
                              }}
                            >
                              <BookmarkButton
                                isFlagged={activity.is_flagged}
                                onClick={() => handleFlagToggle(activity, activity.is_flagged)}
                                title={activity.is_flagged ? 'Remove from saved' : 'Save activity'}
                              />

                              {hasFeatureTag && (
                                <span
                                  className={styles.featureTagBadge}
                                  style={{
                                    backgroundColor:
                                      activity?.feature_tag?.background_color ||
                                      "#008080",
                                    color:
                                      activity?.feature_tag?.font_color ||
                                      "#FFFFFF",
                                  }}
                                >
                                  {activity.feature_tag.tag_name} ★
                                </span>
                              )}

                              <div className={styles.cardHeader}>
                                <div className={styles.cardIconContainer}>
                                  <img
                                    src={categoryTag.icon}
                                    alt="category icon"
                                    className={styles.categoryIcon}
                                  />
                                </div>
                                <div className={styles.cardTitleContainer}>
                                  <div className={styles.titleRow}>
                                    <h3
                                      className={styles.cardTitle}
                                      title={activity.name}
                                    >
                                      {displayName}
                                    </h3>
                                  </div>
                                  <div
                                    className={styles.cardMetaInfo}
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      gap: "12px",
                                      marginLeft: "-5px",
                                    }}
                                  >
                                    <div className={styles.cardMetaItem}>
                                      <img
                                        src={coinSvg}
                                        alt="coin"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                      <span className={styles.metaText}>
                                        {activity.on_request
                                          ? "On-Request"
                                          : `${
                                              tab === "claimed" ||
                                              tab === "pending"
                                                ? activity.credits_used
                                                : activity.credit_cost
                                            } Credits`}
                                      </span>
                                    </div>
                                    <div className={styles.cardMetaItem}>
                                      <img
                                        src={clockPng}
                                        alt="clock"
                                        style={{
                                          width: "18px",
                                          height: "22px",
                                        }}
                                      />
                                      <span className={styles.metaText}>
                                        {activity.is_one_time_activity
                                          ? "One-time"
                                          : `${activity.activity_duration} Days`}
                                      </span>
                                    </div>
                                  </div>

                                  <span
                                    className={styles.categoryTag}
                                    style={{
                                      backgroundColor: categoryTag.bg,
                                      color: categoryTag.color,
                                      border: `1px solid ${categoryTag.border}`,
                                    }}
                                  >
                                    {categoryTag.text}
                                  </span>
                                </div>
                              </div>

                              <div className={styles.cardFooter}>
                                {tab === "pending" ? (
                                  <>
                                    <button
                                      className={styles.knowMoreButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditModal(activity);
                                      }}
                                    >
                                      Edit date
                                    </button>
                                    <button
                                      className={styles.claimButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCancelModal(activity);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const mappedActivity = {
                                          ...activity,
                                          id: activityId,
                                          activity_id:
                                            activity.activity_id || activityId,
                                          activity_name:
                                            activity.activity_name ||
                                            activity.name,
                                          activity_description:
                                            activity.activity_description ||
                                            activity.description,
                                          activity_category:
                                            activity.activity_category ||
                                            activity.category,
                                          activity_duration:
                                            activity.activity_duration ||
                                            activity.claimed_activity_duration,
                                          execution_date:
                                            activity.execution_date ||
                                            activity.claimed_execution_date,
                                          execution_start_date:
                                            activity.execution_start_date,
                                          created_at:
                                            activity.created_at ||
                                            activity.claimed_created_at,
                                          kpis: buildKpisWithExecutionsForActivity(
                                            activityId,
                                          ),
                                        };
                                        setSelectedActivity(mappedActivity);
                                        setShowActivityDetailsModal(true);
                                      }}
                                      className={styles.knowMoreButton}
                                    >
                                      Know More
                                    </button>

                                    {activity.claimable && (
                                      <button
                                        className={styles.claimButton}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClaim(activity);
                                        }}
                                      >
                                        Claim
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()
                      )}
                    </div>
                  )}

                  {/* Pagination Controls */}
                  {totalCount > 0 &&
                    (() => {
                      const totalPages = Math.max(
                        1,
                        Math.ceil(totalCount / pageSize),
                      );
                      const isFirstPage = currentPage === 0;
                      const isLastPage = currentPage >= totalPages - 1;

                      return (
                        <div className={styles.paginationContainer}>
                          <button
                            onClick={() =>
                              !isFirstPage &&
                              setCurrentPage(Math.max(0, currentPage - 1))
                            }
                            disabled={isFirstPage}
                            className={`${styles.pageButton} ${isFirstPage ? styles.pageButtonDisabled : ""}`}
                            onMouseEnter={(e) => {
                              if (!isFirstPage) {
                                e.target.style.backgroundColor =
                                  "rgba(0, 0, 0, 0.3)";
                                e.target.style.transform = "translateY(-1px)";
                                e.target.style.color = "#ffffff";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isFirstPage) {
                                e.target.style.backgroundColor =
                                  "rgba(255, 255, 255, 0.2)";
                                e.target.style.transform = "translateY(0)";
                                e.target.style.color = "#1a3c34";
                              }
                            }}
                          >
                            ← Previous
                          </button>

                          <div className={styles.pageMeta}>
                            <span className={styles.pageLabel}>Page</span>
                            <span className={styles.pageCurrent}>
                              {currentPage + 1}
                            </span>
                            <span className={styles.pageOf}>
                              of {totalPages}
                            </span>
                          </div>

                          <button
                            onClick={() =>
                              !isLastPage &&
                              setCurrentPage(
                                Math.min(totalPages - 1, currentPage + 1),
                              )
                            }
                            disabled={isLastPage}
                            className={`${styles.pageButton} ${isLastPage ? styles.pageButtonDisabled : ""}`}
                            onMouseEnter={(e) => {
                              if (!isLastPage) {
                                e.target.style.backgroundColor =
                                  "rgba(26, 24, 24, 0.3)";
                                e.target.style.transform = "translateY(-1px)";
                                e.target.style.color = "#ffffff";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isLastPage) {
                                e.target.style.backgroundColor =
                                  "rgba(255, 255, 255, 0.2)";
                                e.target.style.transform = "translateY(0)";
                                e.target.style.color = "#1a3c34";
                              }
                            }}
                          >
                            Next →
                          </button>
                        </div>
                      );
                    })()}
                </section>
              </div>

              {/* Secondary Column */}
              <aside className={styles.secondaryColumn}>
                {/* Activities Panel */}
                <section className={styles.activitiesPanel}>
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        margin: "0 auto 1rem",
                        background:
                          "linear-gradient(135deg, #00bfae 0%, #076e72 100%)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={walletSvg}
                        alt="wallet"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                          transform: "scale(3.6)",
                          transformOrigin: "center",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "22px",
                        marginBottom: "0.5rem",
                        fontWeight: 500,
                        // textTransform: "uppercase",
                        letterSpacing: "1px",
                      }}
                    >
                      Current Balance
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <img
                        src="/images/credit icon.svg"
                        alt="credit"
                        style={{ width: "28px", height: "28px" }}
                      />
                      <h2
                        style={{
                          color: "#1f2937",
                          fontSize: "3rem",
                          fontWeight: 600,
                          margin: 0,
                          background:
                            "linear-gradient(135deg, #00bfae 0%, #076e72 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        {Number.isInteger(
                          Number(walletInfo?.wallet_balance ?? 0),
                        )
                          ? Number(walletInfo?.wallet_balance ?? 0).toString()
                          : Number(walletInfo?.wallet_balance ?? 0).toFixed(2)}
                      </h2>
                    </div>
                    <button
                      onClick={() => navigate(`/wallet?brand_id=${brand?.id}`)}
                      style={{
                        marginTop: "16px",
                        width: "100%",
                        padding: "12px 16px",
                        background:
                          "linear-gradient(135deg, #00bfae 0%, #076e72 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Wallet Dashboard
                    </button>
                  </div>

                  {/* Recent Transactions (Top 3) */}
                  <div className={styles.activityGroup}>
                    <div className={styles.activityHeader}>
                      <h3 style={{ fontSize: "22px", fontWeight: 500 }}>
                        Recent Transactions
                      </h3>
                    </div>
                    <div className="activity-list">
                      {!(
                        walletInfo?.recent_transactions &&
                        walletInfo.recent_transactions.length
                      ) ? (
                        <div
                          style={{
                            color: "#888",
                            fontSize: "14px",
                            padding: "12px 0",
                            borderTop: "1px solid #e6e6e6",
                          }}
                        >
                          No recent transactions
                        </div>
                      ) : (
                        walletInfo.recent_transactions.slice(0, 3).map((t) => {
                          const isCredit = t.transaction_type === "credit";
                          const amount = parseFloat(t.amount || 0);
                          const status = (t.status || "").toString();
                          const dateStr = t.created_at
                            ? new Date(t.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : "";
                          return (
                            <div
                              key={t.id}
                              className={styles.activityItemCompact}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {/* Top row: description (left) and amount (right) */}
                              <div className={styles.txnRowTop}>
                                <span className={styles.txnDesc}>
                                  {t.description || "Wallet transaction"}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: isCredit ? "#10b981" : "#ef4444",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {isCredit ? "+" : "-"}
                                  {amount.toFixed(2)}
                                </span>
                              </div>

                              {/* Bottom row: date (left) and status (right) */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: "13px",
                                    color: "#666",
                                    margin: 0,
                                  }}
                                >
                                  {dateStr}
                                </p>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    backgroundColor:
                                      status.toLowerCase() === "success"
                                        ? "rgba(16,185,129,0.1)"
                                        : status.toLowerCase() === "pending"
                                          ? "rgba(234,179,8,0.1)"
                                          : "rgba(239,68,68,0.1)",
                                    color:
                                      status.toLowerCase() === "success"
                                        ? "#10b981"
                                        : status.toLowerCase() === "pending"
                                          ? "#eab308"
                                          : "#ef4444",
                                    display: "inline-block",
                                  }}
                                >
                                  {status.charAt(0).toUpperCase() +
                                    status.slice(1)}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </section>

                {/* Chatbot Panel */}
                {/* <ChatbotComponent
                  onMessageSend={handleChatMessage}
                  avatarSrc="/images/tara-avatar.png"
                  sendIconSrc="/images/chat-send-icon.png"
                /> */}
              </aside>
            </div>
          </main>
        </div>
      </div>

      {/* Popups/Modals - Keep all existing modals */}
      <Modal
        open={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        title=""
        width="40%"
      >
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.95) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: "0",
            margin: "-32px",
            minHeight: "400px",
            position: "relative",
            overflow: "hidden",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.6)",
          }}
        >
          {/* Header with close button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "24px 24px 0 24px",
              position: "relative",
              zIndex: 10,
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#1a3c34",
                margin: 0,
              }}
            >
              Claim Activity
            </h2>
            <button
              onClick={() => setShowClaimModal(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#666",
                padding: "4px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Activity Info */}
          <div
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              margin: "24px",
              borderRadius: "16px",
              padding: "20px",
              backdropFilter: "blur(15px)",
              WebkitBackdropFilter: "blur(15px)",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1a3c34",
                marginBottom: "16px",
              }}
            >
              {selectedActivity?.name}
            </h3>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "#17a2b8",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "white",
                  }}
                >
                  📂
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: "#333" }}>
                    Category:
                  </span>
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    {selectedActivity?.category}
                  </span>
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "#6f42c1",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "white",
                  }}
                >
                  🏷️
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: "#333" }}>Type:</span>
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    {selectedActivity?.type}
                  </span>
                </div>
              </div>
            </div>

            {/* Date Selection */}
            <div
              style={{
                backgroundColor: "rgba(0, 191, 174, 0.05)",
                borderRadius: "12px",
                padding: "16px",
                border: "1px solid rgba(0, 191, 174, 0.1)",
                marginBottom: "20px",
              }}
            >
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  color: "#1a3c34",
                  marginBottom: "8px",
                  fontSize: "16px",
                }}
              >
                Select execution date:
              </label>
              <input
                type="date"
                value={claimDate}
                onChange={(e) => setClaimDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #e6f4f1",
                  fontSize: "14px",
                  backgroundColor: "white",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                  outline: "none",
                }}
              />
            </div>

            {/* Submit Button */}
            <button
              style={{
                width: "100%",
                background: claiming
                  ? "#6c757d"
                  : "linear-gradient(135deg, #00bfae 0%, #076e72 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                padding: "14px 24px",
                fontWeight: 600,
                fontSize: "16px",
                cursor: claiming ? "not-allowed" : "pointer",
                opacity: claiming ? 0.7 : 1,
                boxShadow: claiming
                  ? "none"
                  : "0 4px 12px rgba(0, 191, 174, 0.3)",
                transition: "all 0.2s ease",
              }}
              onClick={() => claimActivity(selectedActivity, claimDate)}
              disabled={claiming}
            >
              {claiming ? "Submitting..." : "Submit Claim"}
            </button>
          </div>
        </div>
      </Modal>

      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filterCategory={filterCategory}
        filterCreditsRange={clampedCreditsRange}
        creditsBounds={creditsBounds}
        filterDurationRange={clampedDurationRange}
        durationBounds={durationBounds}
        categories={categories}
        onApplyFilters={({ category, creditsRange, durationRange }) => {
          setFilterCategory(category);
          setFilterCreditsRange(creditsRange);
          setFilterDurationRange(durationRange);
          setCurrentPage(0);
          setShowFilterModal(false);
        }}
      />
      {/* Add locked upgrade modal */}
      {/* <Modal open={showLockUpgradeModal} onClose={() => setShowLockUpgradeModal(false)} title="Upgrade Required">
        <div style={{ marginBottom: 16 }}>
          <b>{selectedActivity?.name}</b><br />
          <div style={{ margin: '12px 0' }}>This activity is locked. Upgrade to the <b>{selectedActivity?.plan_name || selectedActivity?.type || 'required plan'}</b> plan to unlock and use this activity.</div>
        </div>
        <button style={{ background: '#00bfae', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>View Plans</button>
      </Modal> */}

      {/* Activity Details Modal */}
      <Modal
        open={showActivityDetailsModal}
        onClose={() => setShowActivityDetailsModal(false)}
        title=""
        width="50%"
      >
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.95) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: "0",
            margin: "-32px",
            minHeight: "600px",
            position: "relative",
            overflow: "hidden",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "none",
          }}
        >
          {/* Header with close button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "24px 24px 0 24px",
              position: "relative",
              zIndex: 10,
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#1a3c34",
                margin: 0,
              }}
            >
              {selectedActivity?.activity_name ||
                selectedActivity?.name ||
                "Activity Details"}
            </h2>
            <button
              onClick={() => setShowActivityDetailsModal(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#666",
                padding: "4px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Subtitle */}
          <p
            style={{
              fontSize: "14px",
              color: "#666",
              margin: "8px 24px 24px",
              fontWeight: 500,
            }}
          >
            {selectedActivity?.activity_category ||
              selectedActivity?.category ||
              "Marketing"}{" "}
            activity
          </p>

          {/* Activity Details Section */}
          <div
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              margin: "0 24px 20px",
              borderRadius: "16px",
              padding: "20px",
              backdropFilter: "blur(15px)",
              WebkitBackdropFilter: "blur(15px)",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#1a3c34",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#00bfae",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  color: "white",
                }}
              >
                ℹ
              </span>
              Activity Details
            </h3>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#6c757d",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "white",
                  }}
                >
                  📅
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: "#333" }}>Date:</span>
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    {selectedActivity?.execution_date
                      ? `Execution Date: ${selectedActivity.execution_date}`
                      : selectedActivity?.execution_start_date
                        ? `Start Date: ${selectedActivity.execution_start_date}`
                        : "*Will be scheduled once activity is claimed."}
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#17a2b8",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "white",
                    marginTop: "2px",
                  }}
                >
                  📝
                </span>
                <div style={{ flex: 1 }}>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#333",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    Description:
                  </span>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      lineHeight: "1.5",
                      margin: 0,
                    }}
                  >
                    {selectedActivity?.activity_description ||
                      selectedActivity?.description ||
                      "A comprehensive activity designed to boost your brand visibility and engagement. This activity will be executed by our expert team according to your brand guidelines and objectives."}
                  </p>
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                {/* Display activity duration and credit cost */}
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#17a2b8",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "white",
                    marginTop: "2px",
                  }}
                >
                  ⏳
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: "#333" }}>
                    Duration:{" "}
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginLeft: "4px",
                    }}
                  >
                    {selectedActivity?.is_one_time_activity
                      ? "One-time Activity"
                      : `${selectedActivity?.activity_duration} days`}
                  </span>
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#17a2b8",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "white",
                    marginTop: "2px",
                  }}
                >
                  💰
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: "#333" }}>
                    Credits:{" "}
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      marginLeft: "4px",
                    }}
                  >
                    {selectedActivity?.on_request
                      ? "On-Request"
                      : tab === "claimed" ||
                          tab === "pending" ||
                          tab === "cancelled"
                        ? selectedActivity?.credits_used
                        : selectedActivity?.credit_cost}
                  </span>
                </div>
              </div>

              {/* Activity Tags */}
              {selectedActivity?.tags &&
                selectedActivity.tags.filter((tag) => {
                  const normalizedTag = String(tag?.name || "").toLowerCase();
                  return (
                    !normalizedTag.includes("pinned") &&
                    normalizedTag !== "trending" &&
                    normalizedTag !== "popular"
                  );
                }).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#6f42c1",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "white",
                        marginTop: "2px",
                      }}
                    >
                      🏷️
                    </span>
                    <div style={{ flex: 1 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#333",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        Tags:
                      </span>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        {selectedActivity.tags
                          .filter((tag) => {
                            const normalizedTag = String(
                              tag?.name || "",
                            ).toLowerCase();
                            return (
                              !normalizedTag.includes("pinned") &&
                              normalizedTag !== "trending" &&
                              normalizedTag !== "popular"
                            );
                          })
                          .map((tag) => (
                            <span
                              key={tag.id || tag.name}
                              style={{
                                backgroundColor: "#e3f2fd",
                                color: "#1976d2",
                                borderRadius: "12px",
                                padding: "4px 12px",
                                fontSize: "12px",
                                fontWeight: 500,
                                border: "1px solid #bbdefb",
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Single Deployment History Section */}
          {() => {
            // Get all kpis from activity
            const kpis = selectedActivity?.kpis || [];

            // kpi_data.append({
            //     'id': kpi.id,
            //     'name': kpi.name,
            //     'description': kpi.description,
            //     'measurement_unit': kpi.measurement_unit,
            //     'input_type': kpi.input_type,
            //     'options': format_json_as_key_value_pairs(kpi.options),
            //     'is_required': kpi.is_required,
            //     'value': kpi_value if kpi_value else None
            // })

            // If no kpis found, show a single section with current activity kpis
            if (!kpis.length) {
              return (
                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    margin: "0 24px 20px",
                    borderRadius: "16px",
                    padding: "20px",
                    backdropFilter: "blur(15px)",
                    WebkitBackdropFilter: "blur(15px)",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#1a3c34",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#6f42c1",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "white",
                      }}
                    >
                      📊
                    </span>
                    Deployment History
                  </h3>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#6c757d",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: "white",
                        }}
                      >
                        📅
                      </span>
                      <div>
                        <span style={{ fontWeight: 600, color: "#333" }}>
                          Created:
                        </span>
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          {selectedActivity?.created_at
                            ? new Date(
                                selectedActivity.created_at,
                              ).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#17a2b8",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: "white",
                        }}
                      >
                        ⚡
                      </span>
                      <div>
                        <span style={{ fontWeight: 600, color: "#333" }}>
                          Duration:
                        </span>
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          {selectedActivity?.activity_duration || "N/A"} days
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#dc3545",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: "white",
                        }}
                      >
                        📈
                      </span>
                      <div>
                        <span style={{ fontWeight: 600, color: "#333" }}>
                          Analytics:
                        </span>
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "14px",
                            color: "#888",
                          }}
                        >
                          *Data is aggregated from Meta dashboard
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // Show single deployment history section
            return (
              <div
                key={`${selectedActivity.claim_id}-${selectedActivity.execution_date}`}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  margin: "0 24px 20px",
                  borderRadius: "16px",
                  padding: "20px",
                  backdropFilter: "blur(15px)",
                  WebkitBackdropFilter: "blur(15px)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#1a3c34",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "24px",
                      height: "24px",
                      backgroundColor: "#6f42c1",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      color: "white",
                    }}
                  >
                    📊
                  </span>
                  Deployment History
                </h3>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: (() => {
                          if (selectedActivity.status === "completed")
                            return "#28a745";
                          if (selectedActivity.status === "pending")
                            return "#ffc107";
                          if (selectedActivity.status === "cancelled")
                            return "#dc3545";
                          return "#6c757d";
                        })(),
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "white",
                      }}
                    >
                      {(() => {
                        if (selectedActivity.status === "completed") return "✓";
                        if (selectedActivity.status === "pending") return "⏳";
                        if (selectedActivity.status === "cancelled") return "✕";
                        return "?";
                      })()}
                    </span>
                    <div>
                      <span style={{ fontWeight: 600, color: "#333" }}>
                        Status:
                      </span>
                      <span
                        style={{
                          marginLeft: "8px",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          backgroundColor: (() => {
                            if (selectedActivity.status === "completed")
                              return "#d4edda";
                            if (selectedActivity.status === "pending")
                              return "#fff3cd";
                            if (selectedActivity.status === "cancelled")
                              return "#f8d7da";
                            return "#e2e3e5";
                          })(),
                          color: (() => {
                            if (selectedActivity.status === "completed")
                              return "#155724";
                            if (selectedActivity.status === "pending")
                              return "#856404";
                            if (selectedActivity.status === "cancelled")
                              return "#721c24";
                            return "#6c757d";
                          })(),
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        {(() => {
                          if (selectedActivity.status === "completed")
                            return "Completed";
                          if (selectedActivity.status === "pending")
                            return "Pending";
                          if (selectedActivity.status === "cancelled")
                            return "Cancelled";
                          return "Unknown";
                        })()}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#6c757d",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "white",
                      }}
                    >
                      📅
                    </span>
                    <div>
                      <span style={{ fontWeight: 600, color: "#333" }}>
                        Execution Date:
                      </span>
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        {new Date(
                          selectedActivity.execution_date,
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#17a2b8",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "white",
                      }}
                    >
                      ⚡
                    </span>
                    <div>
                      <span style={{ fontWeight: 600, color: "#333" }}>
                        Duration:
                      </span>
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        {selectedActivity?.activity_duration || "N/A"} days
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#dc3545",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "white",
                      }}
                    >
                      📈
                    </span>
                    <div>
                      <span style={{ fontWeight: 600, color: "#333" }}>
                        Analytics:
                      </span>
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "14px",
                          color: "#888",
                        }}
                      >
                        *Data is aggregated from Meta dashboard
                      </span>
                    </div>
                  </div>

                  {/* KPI Metrics for this specific execution */}
                  {kpis.length > 0 && (
                    <>
                      <div style={{ marginTop: "20px", marginBottom: "16px" }}>
                        <h4
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1a3c34",
                            marginBottom: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              width: "20px",
                              height: "20px",
                              backgroundColor: "#28a745",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              color: "white",
                            }}
                          >
                            📊
                          </span>
                          KPI Metrics -{" "}
                          {selectedActivity?.activity_category ||
                            selectedActivity?.category ||
                            "Performance Marketing"}
                        </h4>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: "12px",
                            marginTop: "16px",
                          }}
                        >
                          {kpis.map((kpi, index) => (
                            <div
                              key={index}
                              style={{
                                backgroundColor: "rgba(0, 191, 174, 0.05)",
                                borderRadius: "12px",
                                padding: "16px",
                                border: "1px solid rgba(0, 191, 174, 0.1)",
                                textAlign: "center",
                                transition:
                                  "transform 0.2s ease, box-shadow 0.2s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "translateY(-2px)";
                                e.target.style.boxShadow =
                                  "0 8px 25px rgba(0, 191, 174, 0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "translateY(0)";
                                e.target.style.boxShadow = "none";
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "24px",
                                  fontWeight: 700,
                                  color: "#00bfae",
                                  marginBottom: "4px",
                                }}
                              >
                                {(() => {
                                  const value = kpi.value;
                                  const name = kpi.name || kpi.kpi;
                                  const valueJson = kpi.value_json;

                                  // If value_json exists and has key-value pairs, display them in green boxes
                                  if (
                                    valueJson &&
                                    Array.isArray(valueJson) &&
                                    valueJson.length > 0
                                  ) {
                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          flexWrap: "wrap",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {valueJson.map((pair, index) => (
                                          <div
                                            key={index}
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                backgroundColor:
                                                  "rgba(0, 191, 174, 0.05)",
                                                borderRadius: "12px",
                                                padding: "8px 12px",
                                                border:
                                                  "1px solid rgba(0, 191, 174, 0.1)",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#00bfae",
                                              }}
                                            >
                                              {pair.key}
                                            </div>
                                            <div
                                              style={{
                                                backgroundColor:
                                                  "rgba(0, 191, 174, 0.05)",
                                                borderRadius: "12px",
                                                padding: "8px 12px",
                                                border:
                                                  "1px solid rgba(0, 191, 174, 0.1)",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#1a3c34",
                                              }}
                                            >
                                              {pair.value}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }

                                  // Fallback to regular value display
                                  if (
                                    value === null ||
                                    value === undefined ||
                                    value === ""
                                  ) {
                                    return "N/A";
                                  }

                                  let displayValue = value;
                                  if (
                                    typeof value === "number" &&
                                    value >= 1000
                                  ) {
                                    displayValue =
                                      (value / 1000).toFixed(1) + "K";
                                  }

                                  // Add units based on KPI name
                                  if (
                                    name.includes("Rate") ||
                                    name.includes("CTR")
                                  ) {
                                    displayValue += "%";
                                  } else if (
                                    name.includes("CPC") ||
                                    name.includes("CAC") ||
                                    name.includes("EMV")
                                  ) {
                                    displayValue = "₹" + displayValue;
                                  } else if (name.includes("ROAS")) {
                                    displayValue += "x";
                                  }

                                  return displayValue;
                                })()}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  fontWeight: 500,
                                  lineHeight: "1.3",
                                }}
                              >
                                {(() => {
                                  const name = kpi.name || kpi.kpi;
                                  const keyValuePairs = formatKeyValuePairs(
                                    kpi.options,
                                  );

                                  // Debug: Log the options data to see what we're receiving
                                  if (
                                    kpi.options &&
                                    typeof kpi.options === "object"
                                  ) {
                                    console.log("KPI Options Debug:", {
                                      name: name,
                                      options: kpi.options,
                                      formatted: keyValuePairs,
                                    });
                                  }

                                  if (keyValuePairs.length > 0) {
                                    return (
                                      <div>
                                        <div
                                          style={{
                                            marginBottom: "4px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#999",
                                          }}
                                        >
                                          {keyValuePairs.map((pair, index) => (
                                            <span
                                              key={index}
                                              style={{ marginRight: "8px" }}
                                            >
                                              <strong>{pair.key}:</strong>{" "}
                                              {pair.value}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }

                                  // If options exist but couldn't be formatted, show them as key-value pairs
                                  if (
                                    kpi.options &&
                                    typeof kpi.options === "object" &&
                                    !Array.isArray(kpi.options)
                                  ) {
                                    const rawOptions = kpi.options;
                                    const optionPairs = Object.entries(
                                      rawOptions,
                                    )
                                      .slice(0, 5)
                                      .map(([key, value]) => ({
                                        key: key,
                                        value: String(value),
                                      }));

                                    if (optionPairs.length > 0) {
                                      return (
                                        <div>
                                          <div
                                            style={{
                                              marginBottom: "4px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {name}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "10px",
                                              color: "#999",
                                            }}
                                          >
                                            {optionPairs.map((pair, index) => (
                                              <span
                                                key={index}
                                                style={{ marginRight: "8px" }}
                                              >
                                                <strong>{pair.key}:</strong>{" "}
                                                {pair.value}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    }
                                  }

                                  // Check if the KPI value itself contains JSON data that needs to be parsed
                                  if (
                                    kpi.value &&
                                    typeof kpi.value === "string" &&
                                    kpi.value.startsWith("{") &&
                                    kpi.value.endsWith("}")
                                  ) {
                                    try {
                                      const parsedValue = JSON.parse(kpi.value);
                                      if (
                                        typeof parsedValue === "object" &&
                                        parsedValue !== null
                                      ) {
                                        const valuePairs = Object.entries(
                                          parsedValue,
                                        )
                                          .slice(0, 5)
                                          .map(([key, value]) => ({
                                            key: key,
                                            value: String(value),
                                          }));

                                        if (valuePairs.length > 0) {
                                          return (
                                            <div>
                                              <div
                                                style={{
                                                  marginBottom: "4px",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {name}
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "10px",
                                                  color: "#999",
                                                }}
                                              >
                                                {valuePairs.map(
                                                  (pair, index) => (
                                                    <span
                                                      key={index}
                                                      style={{
                                                        marginRight: "8px",
                                                      }}
                                                    >
                                                      <strong>
                                                        {pair.key}:
                                                      </strong>{" "}
                                                      {pair.value}
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          );
                                        }
                                      }
                                    } catch (e) {
                                      // If JSON parsing fails, continue with normal display
                                    }
                                  }

                                  return name;
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div
                          style={{
                            marginTop: "16px",
                            padding: "12px",
                            backgroundColor: "rgba(0, 191, 174, 0.1)",
                            borderRadius: "8px",
                            border: "1px solid rgba(0, 191, 174, 0.2)",
                            fontSize: "12px",
                            color: "#00695c",
                            textAlign: "center",
                          }}
                        >
                          📊 KPI metrics for execution on{" "}
                          {new Date(
                            selectedActivity.execution_date,
                          ).toLocaleDateString()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          }}
        </div>
      </Modal>

      {/* Cancel Pending Claim Modal */}
      <Modal
        open={cancelModalOpen}
        onClose={() => {
          if (cancelling) return;
          setCancelModalOpen(false);
          setClaimToCancel(null);
          cancelReasonRef.current = "";
        }}
        title=""
        width="35%"
      >
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 1) 100%)",
            borderRadius: "24px",
            margin: "-32px",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Cancel activity claim
            </h2>
            <button
              onClick={() => {
                if (cancelling) return;
                setCancelModalOpen(false);
                setClaimToCancel(null);
                cancelReasonRef.current = "";
              }}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: "#6b7280",
              }}
            >
              ×
            </button>
          </div>

          <p
            style={{
              fontSize: "14px",
              color: "#4b5563",
              marginBottom: "16px",
            }}
          >
            This will cancel the pending execution of{" "}
            <strong>{claimToCancel?.activity_name}</strong> scheduled on{" "}
            {claimToCancel?.execution_date
              ? new Date(claimToCancel.execution_date).toLocaleDateString()
              : "the selected date"}
            . Credits used for this claim will be refunded to your wallet.
          </p>

          <label
            style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 600,
              color: "#374151",
              marginBottom: "6px",
            }}
          >
            Reason for cancellation (optional)
          </label>
          <textarea
            defaultValue={cancelReasonRef.current}
            onChange={(e) => {
              cancelReasonRef.current = e.target.value;
            }}
            rows={4}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
              resize: "vertical",
            }}
            disabled={cancelling}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "16px",
            }}
          >
            <button
              onClick={() => {
                if (cancelling) return;
                setCancelModalOpen(false);
                setClaimToCancel(null);
                cancelReasonRef.current = "";
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "999px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                fontSize: "13px",
                color: "#111827",
              }}
              disabled={cancelling}
            >
              Close
            </button>
            <button
              onClick={handleCancelClaim}
              style={{
                padding: "8px 16px",
                borderRadius: "999px",
                border: "1px solid #b91c1c",
                background: "#ef4444",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: 600,
                minWidth: "140px",
                cursor: cancelling ? "not-allowed" : "pointer",
                opacity: cancelling ? 0.7 : 1,
              }}
              disabled={cancelling}
            >
              {cancelling ? "Cancelling..." : "Submit"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Execution Date Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => {
          if (editingDate) return;
          setEditModalOpen(false);
          setClaimToEdit(null);
          setNewExecutionDate("");
          setEditError(null);
        }}
        title=""
        width="35%"
      >
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 1) 100%)",
            borderRadius: "24px",
            margin: "-32px",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Edit execution date
            </h2>
            <button
              onClick={() => {
                if (editingDate) return;
                setEditModalOpen(false);
                setClaimToEdit(null);
                setNewExecutionDate("");
                setEditError(null);
              }}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: "#6b7280",
              }}
            >
              ×
            </button>
          </div>

          <p
            style={{
              fontSize: "14px",
              color: "#4b5563",
              marginBottom: "16px",
            }}
          >
            Update the execution date for{" "}
            <strong>{claimToEdit?.activity_name}</strong>. Wallet credits will
            remain the same.
          </p>

          <label
            style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 600,
              color: "#374151",
              marginBottom: "6px",
            }}
          >
            Execution date
          </label>
          <input
            type="date"
            value={newExecutionDate}
            onChange={(e) => setNewExecutionDate(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
            }}
            disabled={editingDate}
          />
          {editError && (
            <div
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: "#b91c1c",
              }}
            >
              {editError}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "16px",
            }}
          >
            <button
              onClick={() => {
                if (editingDate) return;
                setEditModalOpen(false);
                setClaimToEdit(null);
                setNewExecutionDate("");
                setEditError(null);
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "999px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                fontSize: "13px",
                color: "#111827",
              }}
              disabled={editingDate}
            >
              Close
            </button>
            <button
              onClick={handleUpdateExecutionDate}
              style={{
                padding: "8px 16px",
                borderRadius: "999px",
                border: "1px solid #059669",
                background: "#10b981",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: 600,
                minWidth: "140px",
                cursor: editingDate ? "not-allowed" : "pointer",
                opacity: editingDate ? 0.7 : 1,
              }}
              disabled={editingDate}
            >
              {editingDate ? "Updating..." : "Update date"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
